import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { RefreshCcw, Filter, Loader2, Plus, Flag, AlertCircle, Zap, Users, ClipboardList, Upload, Mail, Sparkles, ArrowUp, ArrowDown, ChevronsUpDown, Trash2, X } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import EmailComposer from "@/components/EmailComposer";

// Firm Jobs pipeline — annual + GST returns.
// Filters + inline status/priority/assignee dropdowns. Create new job + bulk
// GST import from an uploaded Excel (see routes_jobs.bulk-import-gst).

const KIND_LABELS = {
    annual_return: "Annual return",
    gst_return: "GST return",
    provisional_tax: "Provisional tax",
    bs_cleanup: "BS cleanup",
    gst_registration: "GST registration",
    other: "Other",
};
const STATUS_LABELS = {
    data_received: "Data received / ready",
    wip: "Work in progress",
    review: "Under review",
    awaiting_client: "Awaiting client",
    filed: "Filed",
    completed: "Completed",
};
const PRIORITY_LABELS = { normal: "Normal", high: "Top priority", urgent: "Urgent" };
const PRIORITY_STYLES = {
    normal: "bg-slate-100 text-slate-700",
    high: "bg-amber-100 text-amber-800",
    urgent: "bg-red-100 text-red-800",
};
const DATA_SOURCE_LABELS = { xero: "Xero", prosaic: "Prosaic", excel: "Excel", other: "Other" };

export default function FirmJobs() {
    const { user } = useAuth();
    const isAdmin = user?.role === "firm_admin" || user?.role === "super_admin";
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState({});
    const [statuses, setStatuses] = useState([]);
    const [priorities, setPriorities] = useState([]);
    const [sources, setSources] = useState([]);
    const [staff, setStaff] = useState([]);
    const [clients, setClients] = useState([]);
    const [filters, setFilters] = useState({ kind: "all", status: "all", priority: "all", assignee_user_id: "", data_source: "all", q: "" });
    const [sort, setSort] = useState({ by: null, dir: "asc" });
    const [selected, setSelected] = useState(() => new Set());
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [importOpen, setImportOpen] = useState(false);
    const [emailJob, setEmailJob] = useState(null);

    async function load() {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params.set(k, v); });
            if (sort.by) { params.set("sort_by", sort.by); params.set("sort_dir", sort.dir); }
            const { data } = await api.get(`/jobs/?${params.toString()}`);
            setRows(data.rows || []);
            setSummary(data.summary || {});
            setStatuses(data.statuses || []);
            setPriorities(data.priorities || []);
            setSources(data.data_sources || []);
            // Prune selection to rows still visible
            setSelected((prev) => {
                if (!prev.size) return prev;
                const visible = new Set((data.rows || []).map((r) => r.id));
                const next = new Set([...prev].filter((id) => visible.has(id)));
                return next.size === prev.size ? prev : next;
            });
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }

    async function loadStaffAndClients() {
        try {
            const [uRes, cRes] = await Promise.all([
                api.get("/tenants/me/users").catch(() => ({ data: [] })),
                api.get("/clients/").catch(() => ({ data: [] })),
            ]);
            setStaff((uRes.data || []).filter((u) => (u.role || "").startsWith("firm_") || u.role === "super_admin"));
            setClients(cRes.data || []);
        } catch {
            // non-critical
        }
    }

    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filters, sort]);
    useEffect(() => { loadStaffAndClients(); }, []);

    function toggleSort(colKey) {
        setSort((s) => {
            if (s.by !== colKey) return { by: colKey, dir: "asc" };
            if (s.dir === "asc") return { by: colKey, dir: "desc" };
            return { by: null, dir: "asc" };
        });
    }

    function toggleRow(id) {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }
    function toggleAll() {
        setSelected((prev) => {
            const visibleIds = rows.map((r) => r.id);
            const allSelected = visibleIds.every((id) => prev.has(id));
            return allSelected ? new Set() : new Set(visibleIds);
        });
    }

    async function patchJob(id, patch) {
        try {
            await api.patch(`/jobs/${id}`, patch);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    return (
        <div className="p-6 sm:p-8" data-testid="firm-jobs">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">
                        <ClipboardList className="h-3.5 w-3.5" /> Jobs pipeline
                    </div>
                    <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">Jobs</h1>
                    <p className="mt-1 text-sm text-[#5E636E]">
                        Every annual and GST return in one place. New clients get default jobs auto-created.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setImportOpen(true)} className="gap-1.5" data-testid="jobs-import-open">
                        <Upload className="h-3.5 w-3.5" /> Import from NPM
                    </Button>
                    <BackfillButton onDone={load} />
                    <Button variant="outline" onClick={load} className="gap-1.5" data-testid="jobs-refresh">
                        <RefreshCcw className="h-3.5 w-3.5" /> Refresh
                    </Button>
                    <Button onClick={() => setCreateOpen(true)} className="bg-[#0F1115] hover:bg-[#2E2E2E] text-white gap-1.5" data-testid="jobs-create-open">
                        <Plus className="h-3.5 w-3.5" /> New job
                    </Button>
                </div>
            </div>

            {/* Summary tiles — click any tile to filter the pipeline by that segment */}
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-6 gap-3">
                <SummaryTile
                    label="Total" value={summary.total ?? 0} testId="jt-total"
                    onClick={() => setFilters((f) => ({ ...f, kind: "all", status: "all", priority: "all", assignee_user_id: "", q: "" }))}
                    active={filters.kind === "all" && filters.status === "all" && filters.priority === "all" && !filters.assignee_user_id && !filters.q}
                />
                <SummaryTile
                    label="Annual" value={summary.annual ?? 0} testId="jt-annual"
                    onClick={() => setFilters((f) => ({ ...f, kind: "annual_return", status: "all", priority: "all", assignee_user_id: "" }))}
                    active={filters.kind === "annual_return"}
                />
                <SummaryTile
                    label="GST" value={summary.gst ?? 0} testId="jt-gst"
                    onClick={() => setFilters((f) => ({ ...f, kind: "gst_return", status: "all", priority: "all", assignee_user_id: "" }))}
                    active={filters.kind === "gst_return"}
                />
                <SummaryTile
                    label="Urgent" value={summary.urgent ?? 0} tone="red" testId="jt-urgent"
                    onClick={() => setFilters((f) => ({ ...f, priority: "urgent", kind: "all", status: "all", assignee_user_id: "" }))}
                    active={filters.priority === "urgent"}
                />
                <SummaryTile
                    label="Top priority" value={summary.high ?? 0} tone="amber" testId="jt-high"
                    onClick={() => setFilters((f) => ({ ...f, priority: "high", kind: "all", status: "all", assignee_user_id: "" }))}
                    active={filters.priority === "high"}
                />
                <SummaryTile
                    label="Unassigned" value={summary.unassigned ?? 0} testId="jt-unassigned"
                    onClick={() => setFilters((f) => ({ ...f, assignee_user_id: "unassigned", kind: "all", status: "all", priority: "all" }))}
                    active={filters.assignee_user_id === "unassigned"}
                />
            </div>

            {/* Filters */}
            <div className="mt-6 flex flex-wrap items-center gap-2">
                <div className="text-xs text-[#5E636E] flex items-center gap-1.5"><Filter className="h-3.5 w-3.5" /> Filter</div>
                <FilterSelect value={filters.kind} onChange={(v) => setFilters((f) => ({ ...f, kind: v }))} options={[["all", "All kinds"], ["annual_return", "Annual"], ["gst_return", "GST"], ["bs_cleanup", "BS cleanup"], ["gst_registration", "GST registration"], ["other", "Other"]]} testId="jf-kind" />
                <FilterSelect value={filters.status} onChange={(v) => setFilters((f) => ({ ...f, status: v }))} options={[["all", "Any status"], ...statuses.map((s) => [s, STATUS_LABELS[s] || s])]} testId="jf-status" />
                <FilterSelect value={filters.priority} onChange={(v) => setFilters((f) => ({ ...f, priority: v }))} options={[["all", "Any priority"], ...priorities.map((p) => [p, PRIORITY_LABELS[p]])]} testId="jf-priority" />
                <FilterSelect value={filters.assignee_user_id} onChange={(v) => setFilters((f) => ({ ...f, assignee_user_id: v }))} options={[["", "Any assignee"], ["unassigned", "Unassigned"], ...staff.map((s) => [s.id, s.name])]} testId="jf-assignee" />
                <FilterSelect value={filters.data_source} onChange={(v) => setFilters((f) => ({ ...f, data_source: v }))} options={[["all", "Any source"], ["xero", "Xero"], ["prosaic", "Prosaic"], ["excel", "Excel"], ["other", "Other"]]} testId="jf-source" />
                <Input placeholder="Search client…" value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} className="h-8 w-52" data-testid="jf-search" />
                {(sort.by || Object.entries(filters).some(([k, v]) => v && v !== "all" && k !== "q")) && (
                    <button type="button" onClick={() => { setFilters({ kind: "all", status: "all", priority: "all", assignee_user_id: "", data_source: "all", q: "" }); setSort({ by: null, dir: "asc" }); }} className="text-xs text-[#5E636E] hover:text-[#0F1115] underline" data-testid="jf-clear">
                        Clear all
                    </button>
                )}
            </div>

            {/* Bulk actions bar — appears when at least one row is ticked */}
            {selected.size > 0 && (
                <BulkActionsBar
                    count={selected.size}
                    ids={[...selected]}
                    staff={staff}
                    statuses={statuses}
                    priorities={priorities}
                    canDelete={isAdmin}
                    onDone={() => { setSelected(new Set()); load(); }}
                    onClear={() => setSelected(new Set())}
                />
            )}

            {/* Table */}
            <div className="mt-6 rounded-2xl border border-[#EAE7DE] bg-white overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center text-sm text-[#5E636E]"><Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Loading…</div>
                ) : rows.length === 0 ? (
                    <div className="p-8 text-center text-sm text-[#5E636E]">No jobs match this filter.</div>
                ) : (
                    <table className="w-full text-sm" data-testid="jobs-table">
                        <thead className="bg-[#FBFAF6] text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">
                            <tr>
                                <th className="pl-3 pr-1 py-3 text-left w-8">
                                    <input
                                        type="checkbox"
                                        checked={rows.length > 0 && rows.every((r) => selected.has(r.id))}
                                        onChange={toggleAll}
                                        aria-label="Select all"
                                        data-testid="job-select-all"
                                    />
                                </th>
                                <SortableTh label="Client" colKey="client" sort={sort} onSort={toggleSort} />
                                <SortableTh label="Kind / Period" colKey="kind" sort={sort} onSort={toggleSort} />
                                <SortableTh label="Source" colKey="source" sort={sort} onSort={toggleSort} />
                                <SortableTh label="Status" colKey="status" sort={sort} onSort={toggleSort} />
                                <SortableTh label="Priority" colKey="priority" sort={sort} onSort={toggleSort} />
                                <SortableTh label="Assignee" colKey="assignee" sort={sort} onSort={toggleSort} />
                                <SortableTh label="Due" colKey="due" sort={sort} onSort={toggleSort} />
                                <th className="px-3 py-3 text-left">Email</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id} className={"border-t border-[#EAE7DE] " + (r.priority === "urgent" ? "bg-red-50/40" : r.priority === "high" ? "bg-amber-50/30" : "") + (selected.has(r.id) ? " bg-zinc-100/60" : "")} data-testid={`job-row-${r.id}`}>
                                    <td className="pl-3 pr-1 py-2.5 align-top">
                                        <input
                                            type="checkbox"
                                            checked={selected.has(r.id)}
                                            onChange={() => toggleRow(r.id)}
                                            aria-label={`Select ${r.client_name}`}
                                            data-testid={`job-select-${r.id}`}
                                        />
                                    </td>
                                    <td className="px-3 py-2.5 align-top">
                                        <div className="font-medium">{r.client_name}</div>
                                        {r.gst_frequency && <div className="text-[11px] text-[#5E636E] uppercase tracking-wider">{r.gst_frequency.replace("_", "-")}</div>}
                                    </td>
                                    <td className="px-3 py-2.5 align-top">
                                        <div>{r.period_label}</div>
                                        <div className="text-[11px] text-[#5E636E]">{KIND_LABELS[r.kind]}</div>
                                    </td>
                                    <td className="px-3 py-2.5 align-top">
                                        <select value={r.data_source || ""} onChange={(e) => patchJob(r.id, { data_source: e.target.value || null })} className="rounded border border-[#E4E4E7] bg-white px-1.5 h-7 text-[12.5px]" data-testid={`job-source-${r.id}`}>
                                            <option value="">—</option>
                                            {sources.map((s) => <option key={s} value={s}>{DATA_SOURCE_LABELS[s] || s}</option>)}
                                        </select>
                                    </td>
                                    <td className="px-3 py-2.5 align-top">
                                        <select value={r.status} onChange={(e) => patchJob(r.id, { status: e.target.value })} className="rounded border border-[#E4E4E7] bg-white px-1.5 h-7 text-[12.5px] min-w-[160px]" data-testid={`job-status-${r.id}`}>
                                            {statuses.map((s) => <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>)}
                                        </select>
                                    </td>
                                    <td className="px-3 py-2.5 align-top">
                                        <select value={r.priority} onChange={(e) => patchJob(r.id, { priority: e.target.value })} className={"rounded px-1.5 h-7 text-[12px] border border-transparent " + PRIORITY_STYLES[r.priority]} data-testid={`job-priority-${r.id}`}>
                                            {priorities.map((p) => <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>)}
                                        </select>
                                    </td>
                                    <td className="px-3 py-2.5 align-top">
                                        <select value={r.assignee_user_id || ""} onChange={(e) => patchJob(r.id, { assignee_user_id: e.target.value || null })} className="rounded border border-[#E4E4E7] bg-white px-1.5 h-7 text-[12.5px] min-w-[130px]" data-testid={`job-assignee-${r.id}`}>
                                            <option value="">Unassigned</option>
                                            {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                                        </select>
                                    </td>
                                    <td className="px-3 py-2.5 align-top">
                                        <input type="date" value={r.due_date || ""} onChange={(e) => patchJob(r.id, { due_date: e.target.value || null })} className="rounded border border-[#E4E4E7] bg-white px-1.5 h-7 text-[12.5px]" data-testid={`job-due-${r.id}`} />
                                    </td>
                                    <td className="px-3 py-2.5 align-top">
                                        <button
                                            type="button"
                                            onClick={() => setEmailJob(r)}
                                            className="inline-flex items-center gap-1 rounded-md border border-[#E4E4E7] bg-white px-2 h-7 text-[12px] hover:bg-zinc-50"
                                            data-testid={`job-email-btn-${r.id}`}
                                            title={r.last_email_sent_at ? `Last sent ${new Date(r.last_email_sent_at).toLocaleDateString()}` : "Send an email to the client for this job"}
                                        >
                                            <Mail className="h-3 w-3" />
                                            {r.last_email_sent_at ? "Send again" : "Send"}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <CreateJobDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={() => { setCreateOpen(false); load(); }} clients={clients} staff={staff} statuses={statuses} priorities={priorities} sources={sources} />
            <BulkGstImportDialog open={importOpen} onClose={() => setImportOpen(false)} onDone={() => { setImportOpen(false); load(); }} />

            {emailJob && (
                <EmailComposer
                    open={!!emailJob}
                    onClose={() => setEmailJob(null)}
                    onSent={() => { setEmailJob(null); load(); }}
                    title={`Send email · ${emailJob.client_name}`}
                    kind={emailJob.kind === "annual_return" ? "job_annual_ready" : "job_gst_ready"}
                    toEditable
                    loadPreview={async ({ templateId }) => {
                        const params = templateId ? `?template_id=${encodeURIComponent(templateId)}` : "";
                        const { data } = await api.get(`/jobs/${emailJob.id}/email/preview${params}`);
                        return data;
                    }}
                    onSend={async (payload) => {
                        const { data } = await api.post(`/jobs/${emailJob.id}/email/send`, payload);
                        return data;
                    }}
                />
            )}
        </div>
    );
}

function FilterSelect({ value, onChange, options, testId }) {
    return (
        <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="rounded-md border border-[#E4E4E7] bg-white px-2 h-8 text-sm" data-testid={testId}>
            {options.map(([v, l]) => <option key={String(v)} value={v}>{l}</option>)}
        </select>
    );
}

function BackfillButton({ onDone }) {
    const [busy, setBusy] = useState(false);
    async function run() {
        if (!window.confirm("Create default Annual + GST jobs for every existing client that doesn't have one yet? Safe to run multiple times.")) return;
        setBusy(true);
        try {
            const { data } = await api.post("/jobs/backfill-all-clients");
            toast.success(`Scanned ${data.clients_scanned} clients — created ${data.jobs_created} new jobs.`);
            onDone?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }
    return (
        <Button variant="outline" onClick={run} disabled={busy} className="gap-1.5" data-testid="jobs-backfill">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
            {busy ? "Backfilling…" : "Backfill jobs"}
        </Button>
    );
}

function SummaryTile({ label, value, tone, testId, onClick, active }) {
    const bg = tone === "red" ? "bg-red-50 border-red-200 hover:bg-red-100"
        : tone === "amber" ? "bg-amber-50 border-amber-200 hover:bg-amber-100"
        : "bg-white border-[#EAE7DE] hover:bg-zinc-50";
    const activeRing = active ? " ring-2 ring-[#0F1115] ring-offset-1" : "";
    const clickableCursor = onClick ? " cursor-pointer transition" : "";
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={!onClick}
            className={"text-left w-full rounded-xl border p-4 " + bg + clickableCursor + activeRing}
            data-testid={testId}
            title={onClick ? `Filter by ${label}` : undefined}
        >
            <div className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">{label}</div>
            <div className="mt-1 font-display text-2xl font-semibold tabular-nums">{value}</div>
        </button>
    );
}

function CreateJobDialog({ open, onClose, onCreated, clients, staff, statuses, priorities, sources }) {
    const [form, setForm] = useState({
        client_id: "", kind: "annual_return", period_label: "", period_end_date: "",
        due_date: "", data_source: "", gst_frequency: "", priority: "normal", assignee_user_id: "", notes: "",
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => { if (!open) setForm({ client_id: "", kind: "annual_return", period_label: "", period_end_date: "", due_date: "", data_source: "", gst_frequency: "", priority: "normal", assignee_user_id: "", notes: "" }); }, [open]);

    async function submit(e) {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                client_id: form.client_id, kind: form.kind, period_label: form.period_label,
                period_end_date: form.period_end_date || null, due_date: form.due_date || null,
                data_source: form.data_source || null,
                gst_frequency: form.kind === "gst_return" ? (form.gst_frequency || null) : null,
                priority: form.priority,
                assignee_user_id: form.assignee_user_id || null,
                notes: form.notes || null,
            };
            await api.post("/jobs/", payload);
            toast.success("Job created");
            onCreated();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-lg" data-testid="jobs-create-dialog">
                <DialogHeader><DialogTitle>New job</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                    <div>
                        <Label>Client</Label>
                        <select required value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className="mt-1 w-full rounded-md border border-[#E4E4E7] bg-white px-2 h-9 text-sm" data-testid="jc-client">
                            <option value="">Select…</option>
                            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Kind</Label>
                            <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="mt-1 w-full rounded-md border border-[#E4E4E7] bg-white px-2 h-9 text-sm" data-testid="jc-kind">
                                <option value="annual_return">Annual return</option>
                                <option value="gst_return">GST return</option>
                            </select>
                        </div>
                        <div>
                            <Label>Period label</Label>
                            <Input required value={form.period_label} onChange={(e) => setForm({ ...form, period_label: e.target.value })} placeholder="e.g. IR3 2024 or GST Jul-Aug 2025" data-testid="jc-period" className="mt-1" />
                        </div>
                    </div>
                    {form.kind === "gst_return" && (
                        <div>
                            <Label>GST frequency</Label>
                            <select value={form.gst_frequency} onChange={(e) => setForm({ ...form, gst_frequency: e.target.value })} className="mt-1 w-full rounded-md border border-[#E4E4E7] bg-white px-2 h-9 text-sm" data-testid="jc-gstfreq">
                                <option value="">Select…</option>
                                <option value="monthly">Monthly</option>
                                <option value="two_monthly">Two-monthly</option>
                                <option value="six_monthly">Six-monthly</option>
                            </select>
                        </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Period end</Label>
                            <Input type="date" value={form.period_end_date} onChange={(e) => setForm({ ...form, period_end_date: e.target.value })} className="mt-1" data-testid="jc-periodend" />
                        </div>
                        <div>
                            <Label>Due date</Label>
                            <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1" data-testid="jc-due" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Data source</Label>
                            <select value={form.data_source} onChange={(e) => setForm({ ...form, data_source: e.target.value })} className="mt-1 w-full rounded-md border border-[#E4E4E7] bg-white px-2 h-9 text-sm" data-testid="jc-source">
                                <option value="">—</option>
                                {sources.map((s) => <option key={s} value={s}>{DATA_SOURCE_LABELS[s] || s}</option>)}
                            </select>
                        </div>
                        <div>
                            <Label>Priority</Label>
                            <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="mt-1 w-full rounded-md border border-[#E4E4E7] bg-white px-2 h-9 text-sm" data-testid="jc-priority">
                                {priorities.map((p) => <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>)}
                            </select>
                        </div>
                    </div>
                    <div>
                        <Label>Assignee</Label>
                        <select value={form.assignee_user_id} onChange={(e) => setForm({ ...form, assignee_user_id: e.target.value })} className="mt-1 w-full rounded-md border border-[#E4E4E7] bg-white px-2 h-9 text-sm" data-testid="jc-assignee">
                            <option value="">Unassigned</option>
                            {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
                        <Button type="submit" disabled={saving} className="bg-[#0F1115] hover:bg-[#2E2E2E] text-white" data-testid="jc-submit">{saving ? "Saving…" : "Create job"}</Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function BulkGstImportDialog({ open, onClose, onDone }) {
    const [mode, setMode] = useState("full_jobs"); // "full_jobs" | "gst_clients"
    const [defaultSource, setDefaultSource] = useState("xero");
    const [wipeExisting, setWipeExisting] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState(null);
    const [file, setFile] = useState(null);
    const [rows, setRows] = useState([]);

    async function handleFile(f) {
        setFile(f); setResult(null); setRows([]);
        try {
            const XLSX = await import("xlsx").catch(() => null);
            if (XLSX && (f.name.endsWith(".xlsx") || f.name.endsWith(".xls"))) {
                const buf = await f.arrayBuffer();
                const wb = XLSX.read(buf);
                const sheet = wb.Sheets[wb.SheetNames[0]];
                const json = XLSX.utils.sheet_to_json(sheet, { defval: "" });
                setRows(json);
                return;
            }
        } catch (e) { /* fall through */ }
        // CSV fallback
        try {
            const t = await f.text();
            const lines = t.split(/\r?\n/).filter(Boolean);
            const headers = lines.shift().split(",").map((h) => h.replace(/^"|"$/g, "").trim());
            const parsed = lines.map((line) => {
                const cells = line.split(",").map((c) => c.replace(/^"|"$/g, "").trim());
                const o = {}; headers.forEach((h, i) => { o[h] = cells[i] || ""; });
                return o;
            });
            setRows(parsed);
        } catch (e) {
            toast.error("Could not read that file. Please save as .xlsx or .csv and try again.");
        }
    }

    function mapFullJobsRow(r) {
        // NPM jobs export column names → backend NpmJobRow schema
        return {
            customer_name: (r["Customer name"] ?? r.customer_name ?? "").toString().trim(),
            ird: (r["IRD (customer_id)"] ?? r["IRD"] ?? r["Customer ID"] ?? r.ird ?? "").toString().trim() || null,
            email: (r["Email"] ?? r.email ?? "").toString().trim() || null,
            contact_person: (r["Contact person"] ?? r.contact_person ?? "").toString().trim() || null,
            customer_type: (r["Customer type"] ?? r.customer_type ?? "").toString().trim() || null,
            status: (r["Status"] ?? r.status ?? "").toString().trim() || null,
            assigned_to: (r["Assigned to"] ?? r.assigned_to ?? "").toString().trim() || null,
            job_type: (r["Job type"] ?? r.job_type ?? "").toString().trim() || null,
            tax_year: (r["Tax year"] ?? r.tax_year ?? "").toString().trim() || null,
            gst_registered: (r["GST registered"] ?? r.gst_registered ?? "").toString().trim() || null,
            gst_frequency: (r["GST frequency"] ?? r.gst_frequency ?? "").toString().trim() || null,
            gst_period: (r["GST period"] ?? r.gst_period ?? "").toString().trim() || null,
            notes: (r["Notes"] ?? r.notes ?? "").toString().trim() || null,
            stalled: (r["Stalled?"] ?? r["Stalled"] ?? r.stalled ?? "").toString().trim() || null,
            created_at: (r["Created at"] ?? r.created_at ?? "").toString().trim() || null,
            updated_at: (r["Updated at"] ?? r.updated_at ?? "").toString().trim() || null,
        };
    }

    function mapGstClientsRow(r) {
        return {
            customer_id: (r["Customer ID"] ?? r.customer_id ?? "").toString().trim() || null,
            customer_name: (r["Customer name"] ?? r.customer_name ?? "").toString().trim(),
            customer_subtype: (r["Customer subtype"] ?? r.customer_subtype ?? "").toString().trim() || null,
            gst_filing_frequency: (r["GST filing frequency"] ?? r.gst_filing_frequency ?? "").toString().trim() || null,
        };
    }

    async function submit() {
        if (!rows.length) { toast.error("No rows loaded from the file"); return; }
        setUploading(true);
        setResult(null);
        try {
            if (mode === "full_jobs") {
                const payload = {
                    rows: rows.filter((r) => (r["Customer name"] || r.customer_name)).map(mapFullJobsRow),
                    default_data_source: defaultSource,
                    wipe_existing: wipeExisting,
                };
                if (wipeExisting) {
                    if (!window.confirm(`This will DELETE every existing job in your tenant and replace them with the ${payload.rows.length} rows in this file. Continue?`)) {
                        setUploading(false); return;
                    }
                }
                const { data } = await api.post("/jobs/bulk-import-full", payload);
                setResult(data);
                toast.success(`${data.jobs_created} jobs imported · ${data.clients_matched} matched · ${data.clients_created} new`);
            } else {
                const payload = {
                    rows: rows.filter((r) => (r["Customer name"] || r.customer_name)).map(mapGstClientsRow),
                };
                const { data } = await api.post("/jobs/bulk-merge-gst-clients", payload);
                setResult(data);
                toast.success(`${data.clients_matched} clients updated · ${data.clients_created} new`);
            }
            onDone?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setUploading(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-2xl" data-testid="jobs-import-dialog">
                <DialogHeader><DialogTitle>Import from NPM</DialogTitle></DialogHeader>

                {/* Mode tabs */}
                <div className="flex text-sm rounded-full border border-[#E4E4E7] overflow-hidden self-start">
                    <button type="button" onClick={() => { setMode("full_jobs"); setRows([]); setFile(null); setResult(null); }}
                        className={`px-3 py-1.5 ${mode === "full_jobs" ? "bg-[#0F1115] text-white" : "bg-white text-[#5E636E]"}`}
                        data-testid="ji-mode-full">
                        Full jobs list
                    </button>
                    <button type="button" onClick={() => { setMode("gst_clients"); setRows([]); setFile(null); setResult(null); }}
                        className={`px-3 py-1.5 ${mode === "gst_clients" ? "bg-[#0F1115] text-white" : "bg-white text-[#5E636E]"}`}
                        data-testid="ji-mode-gst">
                        GST clients merge
                    </button>
                </div>

                <div className="space-y-3">
                    {mode === "full_jobs" ? (
                        <div className="rounded-md bg-[#FAFAF7] border border-[#E4E4E7] p-3 text-sm text-[#3F444C]">
                            <div className="font-medium text-[#0F1115] mb-1">Full NPM jobs export</div>
                            Upload <code>jobs_YYYYMMDD-HHMM.xlsx</code>. Expected columns: Customer name, IRD (customer_id), Email, Contact person, Customer type, Status, Assigned to, Job type, Tax year, GST registered, GST frequency, GST period, Notes, Stalled?, Created at, Updated at. Missing clients are auto-created; nickname is filled from &quot;Contact person&quot;.
                        </div>
                    ) : (
                        <div className="rounded-md bg-[#FAFAF7] border border-[#E4E4E7] p-3 text-sm text-[#3F444C]">
                            <div className="font-medium text-[#0F1115] mb-1">GST clients merge</div>
                            Upload <code>GST clients.xlsx</code>. Expected columns: Customer ID, Customer name, Customer subtype, GST filing frequency. Matches by IRD → then by name; sets <code>gst_registered=true</code> + <code>gst_frequency</code>. Never creates jobs. Never wipes anything.
                        </div>
                    )}

                    {mode === "full_jobs" && (
                        <>
                            <div>
                                <Label>Default data source (for new clients)</Label>
                                <select value={defaultSource} onChange={(e) => setDefaultSource(e.target.value)} className="mt-1 w-full rounded-md border border-[#E4E4E7] bg-white px-2 h-9 text-sm" data-testid="ji-source">
                                    <option value="xero">Xero</option>
                                    <option value="prosaic">Prosaic</option>
                                    <option value="excel">Excel</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>
                            <label className="flex items-start gap-2 text-[13px] text-[#3F444C] cursor-pointer">
                                <input type="checkbox" checked={wipeExisting} onChange={(e) => setWipeExisting(e.target.checked)} className="mt-1" data-testid="ji-wipe" />
                                <span>
                                    <b>Wipe existing jobs before import.</b> Deletes every current job in this tenant, then inserts the rows in this file. Client records are preserved (never wiped). Recommended when re-syncing the entire NPM state.
                                </span>
                            </label>
                        </>
                    )}

                    <div>
                        <Label>Upload file</Label>
                        <Input type="file" accept=".xlsx,.xls,.csv" onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])} className="mt-1" data-testid="ji-file" />
                        {file && <div className="text-[12px] text-[#5E636E] mt-1">Loaded: <b>{file.name}</b> · {rows.length} rows detected.</div>}
                    </div>

                    {result && (
                        <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm">
                            <div><b>Clients matched:</b> {result.clients_matched}</div>
                            <div><b>Clients created:</b> {result.clients_created}</div>
                            {result.jobs_created != null && <div><b>Jobs created:</b> {result.jobs_created}</div>}
                            {result.wiped_existing && <div className="text-amber-800 mt-1"><b>Existing jobs wiped.</b></div>}
                            {result.skipped?.length > 0 && <div className="text-amber-800 mt-1"><b>Skipped:</b> {result.skipped.length}</div>}
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={uploading}>Close</Button>
                    <Button onClick={submit} disabled={uploading || !rows.length} className="bg-[#0F1115] hover:bg-[#2E2E2E] text-white" data-testid="ji-submit">
                        {uploading ? "Importing…" : mode === "full_jobs" ? `Import ${rows.length} jobs` : `Merge ${rows.length} clients`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function SortableTh({ label, colKey, sort, onSort }) {
    const active = sort.by === colKey;
    const Icon = active ? (sort.dir === "asc" ? ArrowUp : ArrowDown) : ChevronsUpDown;
    return (
        <th className="px-3 py-3 text-left">
            <button
                type="button"
                onClick={() => onSort(colKey)}
                className={"inline-flex items-center gap-1 hover:text-[#0F1115] " + (active ? "text-[#0F1115]" : "")}
                data-testid={`jt-sort-${colKey}`}
                title={`Sort by ${label}`}
            >
                <span>{label}</span>
                <Icon className="h-3 w-3 opacity-70" />
            </button>
        </th>
    );
}


function BulkActionsBar({ count, ids, staff, statuses, priorities, canDelete, onDone, onClear }) {
    const [busy, setBusy] = useState(false);
    const [assignOpen, setAssignOpen] = useState(false);

    async function apply(set, verb) {
        setBusy(true);
        try {
            const { data } = await api.post("/jobs/bulk-update", { ids, set });
            toast.success(`${data.updated} job${data.updated === 1 ? "" : "s"} ${verb}`);
            onDone?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    async function bulkDelete() {
        if (!window.confirm(`Delete ${count} job${count === 1 ? "" : "s"}? This cannot be undone.`)) return;
        setBusy(true);
        try {
            const { data } = await api.post("/jobs/bulk-update", { ids, delete: true });
            toast.success(`Deleted ${data.deleted} job${data.deleted === 1 ? "" : "s"}`);
            onDone?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    return (
        <>
        <div className="mt-3 rounded-xl border border-[#0F1115] bg-[#0F1115] text-white p-2 pr-3 flex items-center gap-2 flex-wrap" data-testid="jobs-bulk-bar">
            <button type="button" onClick={onClear} className="rounded-full p-1 hover:bg-white/10" aria-label="Clear selection" data-testid="jobs-bulk-clear">
                <X className="h-4 w-4" />
            </button>
            <span className="text-sm font-medium pl-1">{count} selected</span>
            <span className="text-xs text-white/60 ml-2">Bulk update →</span>

            <select
                disabled={busy}
                onChange={(e) => e.target.value && apply({ status: e.target.value }, "updated")}
                defaultValue=""
                className="rounded-md bg-white text-[#0F1115] px-2 h-8 text-xs"
                data-testid="jobs-bulk-status"
            >
                <option value="" disabled>Set status…</option>
                {statuses.map((s) => <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>)}
            </select>

            <select
                disabled={busy}
                onChange={(e) => e.target.value && apply({ priority: e.target.value }, "updated")}
                defaultValue=""
                className="rounded-md bg-white text-[#0F1115] px-2 h-8 text-xs"
                data-testid="jobs-bulk-priority"
            >
                <option value="" disabled>Set priority…</option>
                {priorities.map((p) => <option key={p} value={p}>{PRIORITY_LABELS[p] || p}</option>)}
            </select>

            <button
                type="button"
                onClick={() => setAssignOpen(true)}
                disabled={busy}
                className="rounded-md bg-white text-[#0F1115] px-2.5 h-8 text-xs hover:bg-zinc-100"
                data-testid="jobs-bulk-assign-open"
            >
                Assign staff…
            </button>

            <select
                disabled={busy}
                onChange={(e) => e.target.value && apply({ data_source: e.target.value }, "updated")}
                defaultValue=""
                className="rounded-md bg-white text-[#0F1115] px-2 h-8 text-xs"
                data-testid="jobs-bulk-source"
            >
                <option value="" disabled>Set source…</option>
                <option value="xero">Xero</option>
                <option value="prosaic">Prosaic</option>
                <option value="excel">Excel</option>
                <option value="other">Other</option>
            </select>

            <input
                type="date"
                disabled={busy}
                onChange={(e) => e.target.value && apply({ due_date: e.target.value }, "updated")}
                className="rounded-md bg-white text-[#0F1115] px-2 h-8 text-xs"
                data-testid="jobs-bulk-due"
                title="Set due date on selected jobs"
            />

            {canDelete && (
                <button
                    type="button"
                    onClick={bulkDelete}
                    disabled={busy}
                    className="ml-auto inline-flex items-center gap-1 rounded-md bg-red-600 hover:bg-red-700 text-white px-2.5 h-8 text-xs"
                    data-testid="jobs-bulk-delete"
                >
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
            )}
        </div>

        <BulkAssignDialog
            open={assignOpen}
            onClose={() => setAssignOpen(false)}
            staff={staff}
            onPick={(uid) => { setAssignOpen(false); apply({ assignee_user_id: uid }, "reassigned"); }}
        />
        </>
    );
}


function BulkAssignDialog({ open, onClose, staff, onPick }) {
    const [uid, setUid] = useState("");
    useEffect(() => { if (open) setUid(""); }, [open]);
    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="max-w-sm" data-testid="jobs-bulk-assign-dialog">
                <DialogHeader><DialogTitle>Assign staff to selected jobs</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <select
                        value={uid}
                        onChange={(e) => setUid(e.target.value)}
                        className="w-full rounded-md border border-[#E4E4E7] bg-white px-2 h-9 text-sm"
                        data-testid="jobs-bulk-assign-picker"
                    >
                        <option value="">— Unassign —</option>
                        {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button
                        onClick={() => onPick(uid || null)}
                        className="bg-[#0F1115] hover:bg-[#2E2E2E] text-white"
                        data-testid="jobs-bulk-assign-apply"
                    >
                        Apply
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

